import { useCallback, useState, useMemo } from 'react'
import { MAIN_INDICATORS, SUB_INDICATORS, type IndicatorInfo } from '../utils/indicators'
import { getIndicatorFavorites, toggleIndicatorFavorite } from '../utils/indicatorFavorites'
import { Z_CHROME } from './zLayers'

type PanedIndicator = IndicatorInfo & { pane: 'main' | 'sub' }

const byFullName = (a: PanedIndicator, b: PanedIndicator) => a.fullName.localeCompare(b.fullName)

const MAIN_PANED: PanedIndicator[] = MAIN_INDICATORS.map(
  (i): PanedIndicator => ({ ...i, pane: 'main' }),
).sort(byFullName)
const SUB_PANED: PanedIndicator[] = SUB_INDICATORS.map(
  (i): PanedIndicator => ({ ...i, pane: 'sub' }),
).sort(byFullName)
const ALL_PANED: PanedIndicator[] = [...MAIN_PANED, ...SUB_PANED]

/**
 * A host-supplied group of picker rows (e.g. a user's saved scripts). The picker knows
 * nothing about where the items come from: it renders the label, filters items by the
 * search box, and calls `onSelect`. `footer` is an always-visible action row at the end
 * of the group (e.g. "New script") that survives the search filter.
 */
export interface IndicatorPickerGroup {
  label: string
  items: Array<{ key: string; label: string; detail?: string; onSelect: () => void }>
  footer?: { label: string; onSelect: () => void }
  /** Shown in place of the rows when `items` is empty and no search is active. */
  emptyText?: string
}

export interface IndicatorPickerProps {
  onAdd: (name: string, paneType: 'main' | 'sub', calcParams?: unknown[]) => void
  onClose: () => void
  /** Static flow variant — for hosts that position the panel themselves
   *  (e.g. inside a portal popover) instead of the default toolbar-anchored
   *  absolute dropdown. */
  inline?: boolean
  /** Host-owned groups rendered after the catalog groups. */
  extraGroups?: IndicatorPickerGroup[]
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01z" />
    </svg>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </div>
  )
}

function IndicatorRow({
  ind,
  isFav,
  onAdd,
  onToggleFav,
}: {
  ind: PanedIndicator
  isFav: boolean
  onAdd: () => void
  onToggleFav: () => void
}) {
  return (
    <div
      data-indicator-row
      className="group flex items-center gap-1 rounded-sm pr-1.5 hover:bg-accent"
    >
      <button
        aria-label={`Add ${ind.name}`}
        onClick={onAdd}
        className="flex min-w-0 flex-1 items-baseline gap-2 px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors group-hover:text-accent-foreground"
      >
        <span className="min-w-0 flex-1 truncate">{ind.fullName}</span>
        {ind.name !== ind.fullName && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground/60">
            {ind.name}
          </span>
        )}
      </button>
      <button
        aria-label={isFav ? `Unfavorite ${ind.name}` : `Favorite ${ind.name}`}
        title={isFav ? 'Remove from favorites' : 'Add to favorites'}
        onClick={onToggleFav}
        className={
          'flex items-center justify-center p-1 transition-opacity ' +
          (isFav
            ? 'text-amber-400 opacity-100'
            : 'text-muted-foreground opacity-35 group-hover:opacity-100')
        }
      >
        <StarIcon filled={isFav} />
      </button>
    </div>
  )
}

export function IndicatorPicker({ onAdd, onClose, inline, extraGroups }: IndicatorPickerProps) {
  const [query, setQuery] = useState('')
  const [favorites, setFavorites] = useState<string[]>(() => getIndicatorFavorites())

  const toggleFav = (name: string) => {
    setFavorites(toggleIndicatorFavorite(name))
  }

  const matches = useCallback(
    (ind: PanedIndicator) => {
      const q = query.trim().toLowerCase()
      if (!q) return true
      return (
        ind.name.toLowerCase().includes(q) ||
        ind.label.toLowerCase().includes(q) ||
        ind.fullName.toLowerCase().includes(q)
      )
    },
    [query],
  )

  const favRows = useMemo(
    () =>
      favorites
        .map((name) => ALL_PANED.find((i) => i.name === name))
        .filter((i): i is PanedIndicator => i != null && matches(i))
        .sort(byFullName),
    [favorites, matches],
  )
  const mainRows = useMemo(() => MAIN_PANED.filter(matches), [matches])
  const subRows = useMemo(() => SUB_PANED.filter(matches), [matches])

  const q = query.trim().toLowerCase()
  const extra = useMemo(
    () =>
      (extraGroups ?? []).map((g) => ({
        ...g,
        items: q
          ? g.items.filter(
              (i) => i.label.toLowerCase().includes(q) || i.detail?.toLowerCase().includes(q),
            )
          : g.items,
      })),
    [extraGroups, q],
  )
  const extraHasRows = extra.some((g) => g.items.length > 0 || g.footer || (!q && g.emptyText))
  const empty =
    favRows.length === 0 && mainRows.length === 0 && subRows.length === 0 && !extraHasRows

  const renderRow = (ind: PanedIndicator, keyPrefix = '') => (
    <IndicatorRow
      key={keyPrefix + ind.name}
      ind={ind}
      isFav={favorites.includes(ind.name)}
      onAdd={() => {
        onAdd(ind.name, ind.pane, ind.calcParams)
        onClose()
      }}
      onToggleFav={() => toggleFav(ind.name)}
    />
  )

  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      style={inline ? undefined : { zIndex: Z_CHROME }}
      // Square top so the panel reads as an extension of the toolbar button;
      // only the bottom corners and the buttons inside stay rounded.
      className={
        'flex max-h-[380px] w-80 max-w-[90vw] flex-col overflow-hidden ' +
        (inline ? '' : 'bg-popover text-popover-foreground ') +
        (inline
          ? 'rounded-md'
          : 'absolute left-0 top-full mt-1 rounded-md border border-border shadow-md')
      }
    >
      {/* Search */}
      <div className="border-b border-border p-2">
        <input
          autoFocus
          placeholder="Search indicators…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-sm border border-input bg-field px-2 py-1.5 text-xs text-foreground outline-hidden placeholder:text-muted-foreground"
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-1">
        {empty ? (
          <div className="px-3 py-5 text-center text-xs text-muted-foreground">
            No indicators match your search.
          </div>
        ) : (
          <>
            {favRows.length > 0 && (
              <>
                <GroupLabel>Favorites</GroupLabel>
                {favRows.map((ind) => renderRow(ind, 'fav-'))}
                <div className="my-1 h-px bg-border" />
              </>
            )}
            {mainRows.length > 0 && (
              <>
                <GroupLabel>Main Overlay</GroupLabel>
                {mainRows.map((ind) => renderRow(ind))}
              </>
            )}
            {subRows.length > 0 && (
              <>
                {mainRows.length > 0 && <div className="my-1 h-px bg-border" />}
                <GroupLabel>Sub-pane</GroupLabel>
                {subRows.map((ind) => renderRow(ind))}
              </>
            )}
            {extra.map((g) =>
              g.items.length === 0 && !g.footer && (q || !g.emptyText) ? null : (
                <div key={g.label} data-picker-group={g.label}>
                  <div className="my-1 h-px bg-border" />
                  <GroupLabel>{g.label}</GroupLabel>
                  {g.items.map((item) => (
                    <div
                      key={item.key}
                      data-indicator-row
                      className="group flex items-center gap-1 rounded-sm pr-1.5 hover:bg-accent"
                    >
                      <button
                        aria-label={`Add ${item.label}`}
                        onClick={() => {
                          item.onSelect()
                          onClose()
                        }}
                        className="flex min-w-0 flex-1 items-baseline gap-2 px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors group-hover:text-accent-foreground"
                      >
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {item.detail && (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground/60">
                            {item.detail}
                          </span>
                        )}
                      </button>
                    </div>
                  ))}
                  {g.items.length === 0 && !q && g.emptyText && (
                    <div className="px-2.5 py-1.5 text-xs text-muted-foreground/70">
                      {g.emptyText}
                    </div>
                  )}
                  {g.footer && (
                    <button
                      onClick={() => {
                        g.footer!.onSelect()
                        onClose()
                      }}
                      className="mt-0.5 flex w-full items-center rounded-sm px-2.5 py-1.5 text-left text-xs font-medium text-primary hover:bg-accent"
                    >
                      {g.footer.label}
                    </button>
                  )}
                </div>
              ),
            )}
          </>
        )}
      </div>
    </div>
  )
}
